import React, { memo, useState, useRef, useEffect } from "react";
import styled from "styled-components";
import Button from "./Button";
import FormInput from "./FormInput";
import { useProfileStore } from "../store/profileStore";
import { useApiCall } from "../hooks/useApiCall";

const FormRow = styled.div`
  display: flex;
  justify-content: stretch;
  gap: 48px;
  width: 100%;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const FormField = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
`;

const Label = styled.label`
  font-family: "Ubuntu", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.14;
  color: #609f94;
  margin-bottom: 4px;
`;

const DropdownSelect = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  border-radius: 999px;
  border: 1px solid #24ba7d;
  cursor: pointer;
  background-color: transparent;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(36, 186, 125, 0.05);
  }
`;

const DropdownText = styled.span`
  font-family: "Ubuntu", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 1.5;
  color: #ffffff;
`;

const ArrowIcon = styled.div<{ $isOpen: boolean }>`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: ${(props) => (props.$isOpen ? "rotate(180deg)" : "rotate(0)")};
  transition: transform 0.2s ease;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const DropdownContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background-color: #172a2a;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  z-index: 10;
  width: auto;
  overflow: hidden;

  @media (max-width: 768px) {
    position: absolute;
    width: 100%;
  }
`;

const DropdownItem = styled.div<{ $isActive: boolean }>`
  padding: 12px 16px;
  cursor: pointer;
  color: ${(props) => (props.$isActive ? "#24BA7D" : "#FFFFFF")};
  background-color: ${(props) =>
    props.$isActive ? "rgba(36, 186, 125, 0.05)" : "transparent"};

  &:hover {
    background-color: rgba(36, 186, 125, 0.1);
  }

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 32px;

  @media (max-width: 768px) {
    justify-content: center;
    margin-top: 24px;

    button {
      width: 100%;
    }
  }
`;

const PrivacySection = styled.div`
  margin-top: 32px;
  padding-top: 32px;
  border-top: 1px solid #24ba7d;

  h3 {
    font-family: "Ubuntu", sans-serif;
    font-weight: 700;
    font-size: 20px;
    line-height: 1.2;
    color: #ffffff;
    margin-bottom: 24px;
  }
`;

const ProfileForm: React.FC = () => {
  const {
    firstName,
    lastName,
    nickname,
    email,
    vkLink,
    setFirstName,
    setLastName,
    setNickname,
    setEmail,
    setVkLink,
    showVk,
    showTelegram,
    showFirstName,
    showLastName,
    showUserName,
    setShowVk,
    setShowTelegram,
    setShowFirstName,
    setShowLastName,
    setShowUserName,
    updateProfile,
    updateVisionSettings,
    isUpdating,
    isUpdatingVision,
  } = useProfileStore();

  const { executeApiCall } = useApiCall();

  const [vkDropdownOpen, setVkDropdownOpen] = useState(false);
  const [telegramDropdownOpen, setTelegramDropdownOpen] = useState(false);
  const [firstNameDropdownOpen, setFirstNameDropdownOpen] = useState(false);
  const [lastNameDropdownOpen, setLastNameDropdownOpen] = useState(false);
  const [userNameDropdownOpen, setUserNameDropdownOpen] = useState(false);

  const vkRef = useRef<HTMLDivElement>(null);
  const telegramRef = useRef<HTMLDivElement>(null);
  const firstNameRef = useRef<HTMLDivElement>(null);
  const lastNameRef = useRef<HTMLDivElement>(null);
  const userNameRef = useRef<HTMLDivElement>(null);

  // Закрытие выпадающих списков при клике вне
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (vkRef.current && !vkRef.current.contains(event.target as Node)) {
        setVkDropdownOpen(false);
      }
      if (
        telegramRef.current &&
        !telegramRef.current.contains(event.target as Node)
      ) {
        setTelegramDropdownOpen(false);
      }
      if (
        firstNameRef.current &&
        !firstNameRef.current.contains(event.target as Node)
      ) {
        setFirstNameDropdownOpen(false);
      }
      if (
        lastNameRef.current &&
        !lastNameRef.current.contains(event.target as Node)
      ) {
        setLastNameDropdownOpen(false);
      }
      if (
        userNameRef.current &&
        !userNameRef.current.contains(event.target as Node)
      ) {
        setUserNameDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleTelegramDropdown = () => {
    setTelegramDropdownOpen(!telegramDropdownOpen);
  };

  const toggleVkDropdown = () => {
    setVkDropdownOpen(!vkDropdownOpen);
  };

  const toggleFirstNameDropdown = () => {
    setFirstNameDropdownOpen(!firstNameDropdownOpen);
  };

  const toggleLastNameDropdown = () => {
    setLastNameDropdownOpen(!lastNameDropdownOpen);
  };

  const toggleUserNameDropdown = () => {
    setUserNameDropdownOpen(!userNameDropdownOpen);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await executeApiCall(() => updateProfile(), {
      successMessage: "Данные профиля успешно сохранены",
      errorMessage: "Не удалось сохранить данные профиля",
    });
  };

  const handleVisionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await executeApiCall(() => updateVisionSettings(), {
      successMessage: "Настройки приватности успешно сохранены",
      errorMessage: "Не удалось сохранить настройки приватности",
    });
  };

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <FormRow>
          <FormField>
            <FormInput
              label="Имя"
              value={firstName || ""}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Введите имя"
            />
          </FormField>
          <FormField>
            <FormInput
              label="Фамилия"
              value={lastName || ""}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Введите фамилию"
            />
          </FormField>
        </FormRow>

        <FormRow>
          <FormField>
            <FormInput
              label="Никнейм"
              value={nickname || ""}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="Введите никнейм"
            />
          </FormField>
          <FormField>
            <FormInput
              label="Email"
              type="email"
              value={email || ""}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="example@mail.com"
            />
          </FormField>
        </FormRow>

        <FormRow>
          <FormField>
            <FormInput
              label="Ссылка ВКонтакте"
              value={vkLink || ""}
              onChange={(e) => setVkLink(e.target.value)}
              placeholder="https://vk.com/username"
            />
          </FormField>
          <FormField>{/* Пустое поле для баланса */}</FormField>
        </FormRow>

        <ButtonContainer>
          <Button type="submit" disabled={isUpdating} data-tutorial="save-button">
            {isUpdating ? "Сохранение..." : "Сохранить основные данные"}
          </Button>
        </ButtonContainer>
      </form>

      <PrivacySection>
        <h3>Настройки приватности</h3>
        <form onSubmit={handleVisionSubmit}>
          <FormRow>
            <FormField ref={vkRef}>
              <Label>ВКонтакте (Видимость)</Label>
              <DropdownSelect onClick={toggleVkDropdown}>
                <DropdownText>{showVk ? "Открыто" : "Скрыто"}</DropdownText>
                <ArrowIcon $isOpen={vkDropdownOpen}>
                  <svg
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M16.6 7.45834L11.1667 12.8917C10.525 13.5333 9.47499 13.5333 8.83333 12.8917L3.39999 7.45834"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </ArrowIcon>
              </DropdownSelect>

              {vkDropdownOpen && (
                <DropdownContainer>
                  <DropdownItem
                    $isActive={!showVk}
                    onClick={() => {
                      setShowVk(false);
                      setVkDropdownOpen(false);
                    }}
                  >
                    Скрыто
                  </DropdownItem>
                  <DropdownItem
                    $isActive={showVk}
                    onClick={() => {
                      setShowVk(true);
                      setVkDropdownOpen(false);
                    }}
                  >
                    Открыто
                  </DropdownItem>
                </DropdownContainer>
              )}
            </FormField>
            <FormField ref={telegramRef}>
              <Label>Telegram (Видимость)</Label>
              <DropdownSelect onClick={toggleTelegramDropdown}>
                <DropdownText>
                  {showTelegram ? "Открыто" : "Скрыто"}
                </DropdownText>
                <ArrowIcon $isOpen={telegramDropdownOpen}>
                  <svg
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M16.6 7.45834L11.1667 12.8917C10.525 13.5333 9.47499 13.5333 8.83333 12.8917L3.39999 7.45834"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </ArrowIcon>
              </DropdownSelect>

              {telegramDropdownOpen && (
                <DropdownContainer>
                  <DropdownItem
                    $isActive={!showTelegram}
                    onClick={() => {
                      setShowTelegram(false);
                      setTelegramDropdownOpen(false);
                    }}
                  >
                    Скрыто
                  </DropdownItem>
                  <DropdownItem
                    $isActive={showTelegram}
                    onClick={() => {
                      setShowTelegram(true);
                      setTelegramDropdownOpen(false);
                    }}
                  >
                    Открыто
                  </DropdownItem>
                </DropdownContainer>
              )}
            </FormField>
          </FormRow>

          <FormRow>
            <FormField ref={firstNameRef}>
              <Label>Имя (Видимость)</Label>
              <DropdownSelect onClick={toggleFirstNameDropdown}>
                <DropdownText>
                  {showFirstName ? "Открыто" : "Скрыто"}
                </DropdownText>
                <ArrowIcon $isOpen={firstNameDropdownOpen}>
                  <svg
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M16.6 7.45834L11.1667 12.8917C10.525 13.5333 9.47499 13.5333 8.83333 12.8917L3.39999 7.45834"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </ArrowIcon>
              </DropdownSelect>

              {firstNameDropdownOpen && (
                <DropdownContainer>
                  <DropdownItem
                    $isActive={!showFirstName}
                    onClick={() => {
                      setShowFirstName(false);
                      setFirstNameDropdownOpen(false);
                    }}
                  >
                    Скрыто
                  </DropdownItem>
                  <DropdownItem
                    $isActive={showFirstName}
                    onClick={() => {
                      setShowFirstName(true);
                      setFirstNameDropdownOpen(false);
                    }}
                  >
                    Открыто
                  </DropdownItem>
                </DropdownContainer>
              )}
            </FormField>
            <FormField ref={lastNameRef}>
              <Label>Фамилия (Видимость)</Label>
              <DropdownSelect onClick={toggleLastNameDropdown}>
                <DropdownText>
                  {showLastName ? "Открыто" : "Скрыто"}
                </DropdownText>
                <ArrowIcon $isOpen={lastNameDropdownOpen}>
                  <svg
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M16.6 7.45834L11.1667 12.8917C10.525 13.5333 9.47499 13.5333 8.83333 12.8917L3.39999 7.45834"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </ArrowIcon>
              </DropdownSelect>

              {lastNameDropdownOpen && (
                <DropdownContainer>
                  <DropdownItem
                    $isActive={!showLastName}
                    onClick={() => {
                      setShowLastName(false);
                      setLastNameDropdownOpen(false);
                    }}
                  >
                    Скрыто
                  </DropdownItem>
                  <DropdownItem
                    $isActive={showLastName}
                    onClick={() => {
                      setShowLastName(true);
                      setLastNameDropdownOpen(false);
                    }}
                  >
                    Открыто
                  </DropdownItem>
                </DropdownContainer>
              )}
            </FormField>
          </FormRow>

          <FormRow>
            <FormField ref={userNameRef}>
              <Label>Никнейм (Видимость)</Label>
              <DropdownSelect onClick={toggleUserNameDropdown}>
                <DropdownText>
                  {showUserName ? "Открыто" : "Скрыто"}
                </DropdownText>
                <ArrowIcon $isOpen={userNameDropdownOpen}>
                  <svg
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M16.6 7.45834L11.1667 12.8917C10.525 13.5333 9.47499 13.5333 8.83333 12.8917L3.39999 7.45834"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </ArrowIcon>
              </DropdownSelect>

              {userNameDropdownOpen && (
                <DropdownContainer>
                  <DropdownItem
                    $isActive={!showUserName}
                    onClick={() => {
                      setShowUserName(false);
                      setUserNameDropdownOpen(false);
                    }}
                  >
                    Скрыто
                  </DropdownItem>
                  <DropdownItem
                    $isActive={showUserName}
                    onClick={() => {
                      setShowUserName(true);
                      setUserNameDropdownOpen(false);
                    }}
                  >
                    Открыто
                  </DropdownItem>
                </DropdownContainer>
              )}
            </FormField>
            <FormField>{/* Пустое поле для баланса */}</FormField>
          </FormRow>

          <ButtonContainer>
            <Button type="submit" disabled={isUpdatingVision}>
              {isUpdatingVision
                ? "Сохранение..."
                : "Сохранить настройки приватности"}
            </Button>
          </ButtonContainer>
        </form>
      </PrivacySection>
    </div>
  );
};

export default memo(ProfileForm);
